import { normalizePlaceOfIssue } from "./place-of-issue.utils";

export interface ITransactionCertificateReqDto {
  tcId?: string;
  tcNo: string;
  tcVersionNo: string;
  tcStandard: string[];
  tcStatus: string;
  tcAmendmentReason: string;
  tcInvalidationReason: string;
  tcPlaceOfIssue: string;
  tcIssueDate: string;
  tcLastUpdatedDate: string;
  tcCbLicensingCode: string;
  tcApplicationReceivedDate: string;
  tcApplicationCompleteDate: string;
  tcDelayReason: string;
  tcCreatedBy: string;
  tcUsdaNopCompliantFlag: string;
  isVersionUpdated: string;
  tcIsReturn: string;
  returnTcId: string;
  returnTcNo: string;
  returnTcVersionNo: string;
  returnTcSourceVersion: string;
  tcCreationDate: string;
  tcCbLicensingCodeDescription: string;
  // Add description fields
  tcStatusDescription?: string;
  tcAmendmentReasonDescription?: string;
  tcInvalidationReasonDescription?: string;
  tcDelayReasonDescription?: string;
  tcStandardDescription?: string[];
}

export const transactionCertificateReqDto: ITransactionCertificateReqDto = {
  tcNo: "",
  tcAmendmentReason: "",
  tcVersionNo: "",
  tcStandard: [],
  tcStatus: "",
  tcInvalidationReason: "",
  tcPlaceOfIssue: "",
  tcIssueDate: "",
  tcLastUpdatedDate: "",
  tcCbLicensingCode: "",
  tcApplicationReceivedDate: "",
  tcDelayReason: "",
  tcCreatedBy: "",
  tcApplicationCompleteDate: "",
  tcUsdaNopCompliantFlag: "",
  isVersionUpdated: "",
  tcIsReturn: "",
  returnTcId: "",
  returnTcNo: "",
  returnTcVersionNo: "",
  returnTcSourceVersion: "",
  tcCreationDate: "",
  tcCbLicensingCodeDescription: ""
}

// Helper function to convert API tcIsReturn value (Y/N) to form value (Yes/No)
const convertIsReturnValue = (value: string | undefined): string => {
  if (!value) return '';
  const normalized = value.toUpperCase().trim();
  if (normalized === 'Y') return 'Yes';
  if (normalized === 'N') return 'No';
  return value; // Return original if it's already in the correct format
};

// The USDA NOP flag is stored as Y/N (public API contract) but the radio options
// are Yes/No — convert on load so a saved value binds to the radio control.
export const convertUsdaNopValueForForm = (value: string | undefined): string => {
  if (!value) return '';
  const normalized = value.toUpperCase().trim();
  if (normalized === 'Y') return 'Yes';
  if (normalized === 'N') return 'No';
  return value;
};

export const convertUsdaNopValueForApi = (value: string | undefined): string => {
  if (!value) return '';
  const normalized = value.toUpperCase().trim();
  if (normalized === 'YES' || normalized === 'Y') return 'Y';
  if (normalized === 'NO' || normalized === 'N') return 'N';
  return value;
};

export const mapToTransactionCertificateReqDto = (data?: any, TE_ID?: any, isVersionUpdated?: boolean, cbLicenseCode?: any): ITransactionCertificateReqDto => {
  const tcStandards = Array.isArray(data?.tcStandard)
    ? data.tcStandard
    : data?.tcStandard
      ? [data.tcStandard]
      : [];
  return {
    ...(TE_ID ? { tcId: TE_ID } : {}),
    isVersionUpdated: isVersionUpdated ? "yes" : "no",
    tcNo: data.tcNo || '',
    tcVersionNo: data.tcVersionNo || '',
    tcStandard: tcStandards,
    tcStatus: data.tcStatus || '',
    tcStatusDescription: data.tcStatus === 'V' ? 'Valid' :
      data.tcStatus === 'I' ? 'Invalid' :
        data.tcStatus === 'A' ? 'Amended' :
          data.tcStatus === 'R' ? 'Return' :
            data.tcStatus === 'RA' ? 'Return Amended' :
              data.tcStatus === 'RI' ? 'Return Invalidated' : '',
    tcAmendmentReason: data.tcAmendmentReason || '',
    tcInvalidationReason: data.tcInvalidationReason || '',
    tcPlaceOfIssue: normalizePlaceOfIssue(data.tcPlaceOfIssue),
    tcIssueDate: data.tcIssueDate || data.tcDateOfIssue || '',
    tcLastUpdatedDate: data.tcLastUpdatedDate || data.tcLastUpdated || '',
    tcCbLicensingCode: cbLicenseCode || '',
    tcApplicationReceivedDate: data.tcApplicationReceivedDate || data.tcApplicationReceived || '',
    tcApplicationCompleteDate: data.tcApplicationCompleteDate || data.tcApplicationComplete || '',
    tcDelayReason: data.tcDelayReason || '',
    tcCreatedBy: data.tcCreatedBy || '',
    // Clear legacy/stale hidden values when OCS is no longer selected. This
    // also guarantees that the outbound base-details payload is DAT-501 valid.
    tcUsdaNopCompliantFlag: tcStandards.includes('OCS')
      ? convertUsdaNopValueForForm(data.tcUsdaNopCompliantFlag)
      : '',
    tcIsReturn: convertIsReturnValue(data.tcIsReturn),
    returnTcId: data.returnTcId || '',
    returnTcNo: data.returnTcNo || '',
    returnTcVersionNo: data.returnTcVersionNo || '',
    returnTcSourceVersion: data.returnTcSourceVersion || '',
    tcCreationDate: data.tcCreationDate || '',
    tcCbLicensingCodeDescription: data.tcCbLicensingCodeDescription || '',
    // Use API-provided descriptions only. Avoid recomputing with canonical hardcoded
    // option lists, because that can cause description mismatches in payloads.
    tcAmendmentReasonDescription: data.tcAmendmentReasonDescription ?? "",
    tcInvalidationReasonDescription:
      data.tcInvalidationReasonDescription ?? "",
    tcDelayReasonDescription: data.tcDelayReasonDescription ?? "",

    // For multi-select fields
    tcStandardDescription: tcStandards.length
      ? tcStandards.map((code: string) => {
        const option = [
          { label: "Organic Content Standard (OCS)", value: "OCS" },
          { label: "Responsible Down Standard (RDS)", value: "RDS" },
          { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
          { label: "Global Recycled Standard (GRS)", value: "GRS" },
          { label: "Responsible Wool Standard (RWS)", value: "RWS" },
          { label: "Content Claim Standard (CCS)", value: "CCS" },
          { label: "Recycled Claim Standard (RCS)", value: "RCS" },
          { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
          { label: "Sustainable Fiber Alliance (SFA)", value: "SFA" },
        ].find(opt => opt.value === code);
        return option?.label || "";
      })
      : [],
  };
};
